(function (window) {
  'use strict';

  /**
   * Security rules:
   * - Prices come only from Shopify /products/{handle}.js (public storefront).
   * - Never accept a price from the page, URL, or cart payload.
   * - Quotes and cart lines may only use variant IDs present in the loaded catalog.
   * - Admin API tokens must never appear here.
   */

  function emptyCatalog() {
    return {
      loaded: false,
      loading: null,
      currency: null,
      moneyFormat: '${{amount}}',
      byId: {},
      bySku: {},
      byStyle: {},
      byCableKey: {},
      byHandle: {},
      handles: [],
      errors: [],
    };
  }

  function normalizeSku(value) {
    return String(value || '')
      .trim()
      .toUpperCase();
  }

  function normalizeOption(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  function toVariantId(value) {
    var text = String(value || '');
    var gidMatch = text.match(/^gid:\/\/shopify\/ProductVariant\/(\d+)$/);
    if (gidMatch) return gidMatch[1];
    return /^\d+$/.test(text) ? text : null;
  }

  function cableKey(handle, colorValue, lengthMeters) {
    return (
      normalizeOption(handle) +
      '|' +
      normalizeOption(colorValue) +
      '|' +
      String(Number(lengthMeters) || '')
    );
  }

  function collectProductHandles(rules) {
    var handles = {};
    function add(handle) {
      var key = String(handle || '').trim();
      if (!key) return;
      handles[key] = true;
    }
    if (rules.pricing && Array.isArray(rules.pricing.productHandles)) {
      rules.pricing.productHandles.forEach(add);
    }
    if (rules.configuratorVariant) add(rules.configuratorVariant.productHandle);
    if (rules.adapter) add(rules.adapter.productHandle);
    Object.keys(rules.multipleLightingByBranchCount || {}).forEach(function (key) {
      add(rules.multipleLightingByBranchCount[key].productHandle);
    });
    Object.keys(rules.splittersByBranchCount || {}).forEach(function (key) {
      add(rules.splittersByBranchCount[key].productHandle);
    });
    (rules.installations || []).forEach(function (installation) {
      add(installation.handle);
      add(installation.productHandle);
      Object.keys(installation.fixturesByShape || {}).forEach(function (shapeId) {
        (installation.fixturesByShape[shapeId] || []).forEach(function (fixture) {
          add(fixture.productHandle);
        });
      });
    });
    Object.keys(rules.sharedCableByColorAndLength || {}).forEach(function (colorId) {
      Object.keys(rules.sharedCableByColorAndLength[colorId] || {}).forEach(function (length) {
        add(rules.sharedCableByColorAndLength[colorId][length].productHandle);
      });
    });
    return Object.keys(handles);
  }

  function ingestProduct(catalog, product) {
    if (!product || !Array.isArray(product.variants)) return;
    if (product.currency) catalog.currency = product.currency;
    var handle = product.handle || '';
    product.variants.forEach(function (variant) {
      var id = toVariantId(variant.id);
      if (!id) return;
      // product.js uses cents; never trust a client-provided override.
      var priceCents = Math.round(Number(variant.price) || 0);
      var option1 = variant.option1 || '';
      var option2 = variant.option2 || '';
      var option3 = variant.option3 || '';
      if ((!option1 || !option2) && Array.isArray(variant.options)) {
        option1 = variant.options[0] || option1;
        option2 = variant.options[1] || option2;
        option3 = variant.options[2] || option3;
      }
      var title = variant.public_title || variant.title || '';
      var imageUrl = '';
      if (variant.featured_image && variant.featured_image.src) {
        imageUrl = String(variant.featured_image.src);
      } else if (typeof product.featured_image === 'string' && product.featured_image) {
        imageUrl = product.featured_image;
      } else if (product.featured_image && product.featured_image.src) {
        imageUrl = String(product.featured_image.src);
      } else if (Array.isArray(product.images) && product.images[0]) {
        imageUrl =
          typeof product.images[0] === 'string'
            ? product.images[0]
            : String(product.images[0].src || '');
      }
      var entry = {
        id: id,
        priceCents: priceCents,
        compareAtCents: Math.round(Number(variant.compare_at_price) || 0),
        available: variant.available !== false,
        sku: normalizeSku(variant.sku),
        title: title,
        productTitle: product.title || '',
        productHandle: handle,
        option1: option1,
        option2: option2,
        option3: option3,
        imageUrl: imageUrl,
      };
      catalog.byId[id] = entry;
      if (entry.sku) catalog.bySku[entry.sku] = entry;
      if (title) catalog.byStyle[normalizeSku(title)] = entry;
      if (option1) catalog.byStyle[normalizeSku(option1)] = entry;
      // First available variant becomes the product-handle default (Tepo parent product).
      if (handle && !catalog.byHandle[handle]) {
        catalog.byHandle[handle] = entry;
      } else if (
        handle &&
        catalog.byHandle[handle] &&
        catalog.byHandle[handle].available === false &&
        entry.available
      ) {
        catalog.byHandle[handle] = entry;
      }
      // 彩桥线: 颜色 + 长度
      if (handle && option1 && option2) {
        catalog.byCableKey[cableKey(handle, option1, option2)] = entry;
      }
    });
  }

  function fetchProductJson(handle) {
    var url = '/products/' + encodeURIComponent(handle) + '.js';
    return fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (response) {
      if (!response.ok) throw new Error('product_fetch_failed:' + handle + ':' + response.status);
      return response.json();
    });
  }

  function loadCatalog(rules, options) {
    options = options || {};
    var catalog = options.catalog || emptyCatalog();
    if (catalog.loading) return catalog.loading;
    if (catalog.loaded && !options.force) {
      return Promise.resolve(catalog);
    }
    var handles = collectProductHandles(rules);
    catalog.handles = handles;
    catalog.errors = [];
    if (!handles.length) {
      catalog.loaded = true;
      return Promise.resolve(catalog);
    }
    catalog.loading = Promise.all(
      handles.map(function (handle) {
        return fetchProductJson(handle)
          .then(function (product) {
            ingestProduct(catalog, product);
          })
          .catch(function (err) {
            catalog.errors.push(String(err && err.message ? err.message : err));
          });
      })
    ).then(function () {
      catalog.loaded = true;
      catalog.loading = null;
      return catalog;
    });
    return catalog.loading;
  }

  function resolveCatalogEntry(entry, catalog) {
    if (!entry || !catalog) return null;
    var byId = catalog.byId || {};
    var id = toVariantId(entry.cartVariantId || entry.variantId);
    if (id && byId[id]) return byId[id];
    var sku = normalizeSku(entry.sku);
    if (sku && catalog.bySku && catalog.bySku[sku]) return catalog.bySku[sku];
    if (sku && catalog.byStyle && catalog.byStyle[sku]) return catalog.byStyle[sku];
    // Cable fallback: product handle + color + length meters.
    if (entry.productHandle && entry.colorValue && entry.lengthMeters != null && catalog.byCableKey) {
      var key = cableKey(entry.productHandle, entry.colorValue, entry.lengthMeters);
      if (catalog.byCableKey[key]) return catalog.byCableKey[key];
    }
    return null;
  }

  function entryIsMapped(entry) {
    if (!entry) return false;
    return !!(toVariantId(entry.cartVariantId || entry.variantId) || entry.sku);
  }

  function applyVariantMapToRules(rules, map) {
    if (!rules || !map) return rules;
    if (Array.isArray(map.productHandles) && map.productHandles.length) {
      rules.pricing = rules.pricing || {};
      rules.pricing.productHandles = map.productHandles.slice();
    }
    Object.keys(map.cablesByColorAndLength || {}).forEach(function (colorId) {
      Object.keys(map.cablesByColorAndLength[colorId] || {}).forEach(function (length) {
        var mapped = map.cablesByColorAndLength[colorId][length];
        var target =
          rules.sharedCableByColorAndLength &&
          rules.sharedCableByColorAndLength[colorId] &&
          rules.sharedCableByColorAndLength[colorId][length];
        if (!mapped || !target) return;
        target.cartVariantId = mapped.cartVariantId || target.cartVariantId;
        target.variantId = mapped.cartVariantId || target.variantId;
        target.productHandle = mapped.productHandle || target.productHandle || '彩桥线';
        target.sku = mapped.sku || target.sku;
        target.colorValue = mapped.colorValue || target.colorValue;
        target.lengthMeters = mapped.lengthMeters || Number(length);
      });
    });
    (rules.installations || []).forEach(function (installation) {
      var installMapped =
        (map.installationsById && map.installationsById[installation.id]) ||
        (map.installationsByHandle &&
          installation.handle &&
          map.installationsByHandle[installation.handle]);
      if (installMapped) {
        if (installMapped.cartVariantId) {
          installation.cartVariantId = String(installMapped.cartVariantId);
          installation.variantId = String(installMapped.cartVariantId);
        }
        if (installMapped.pluginOptionSetId) {
          installation.pluginOptionSetId = String(installMapped.pluginOptionSetId);
        }
        if (installMapped.productHandle) {
          installation.handle = installMapped.productHandle;
          installation.productHandle = installMapped.productHandle;
        }
      }
      Object.keys(installation.fixturesByShape || {}).forEach(function (shapeId) {
        (installation.fixturesByShape[shapeId] || []).forEach(function (fixture) {
          var mapped = map.fixturesByStyle && map.fixturesByStyle[fixture.id];
          if (!mapped) mapped = map.fixturesByStyle && map.fixturesByStyle[fixture.sku];
          if (!mapped) return;
          fixture.cartVariantId = mapped.cartVariantId;
          fixture.variantId = mapped.cartVariantId;
          fixture.productHandle = mapped.productHandle || 'lighting';
          if (mapped.sku) fixture.sku = mapped.sku;
          if (mapped.priceSnapshot != null) fixture.priceSnapshot = mapped.priceSnapshot;
        });
      });
    });
    if (map.adapter && rules.adapter) {
      rules.adapter.cartVariantId = map.adapter.cartVariantId;
      rules.adapter.variantId = map.adapter.cartVariantId;
      rules.adapter.productHandle = map.adapter.productHandle || rules.adapter.productHandle;
      rules.adapter.sku = map.adapter.sku || rules.adapter.sku;
    }
    Object.keys(map.splittersByBranchCount || {}).forEach(function (count) {
      var mapped = map.splittersByBranchCount[count];
      var target = rules.splittersByBranchCount && rules.splittersByBranchCount[count];
      if (!mapped || !target) return;
      target.cartVariantId = mapped.cartVariantId;
      target.variantId = mapped.cartVariantId;
      target.productHandle = mapped.productHandle || target.productHandle;
      target.sku = mapped.sku || target.sku;
    });
    return rules;
  }

  function formatMoney(cents, moneyFormat) {
    var amount = (Math.max(0, Number(cents) || 0) / 100).toFixed(2);
    var format = moneyFormat || '${{amount}}';
    if (format.indexOf('{{amount_no_decimals}}') !== -1) {
      return format.replace('{{amount_no_decimals}}', String(Math.round(Number(amount))));
    }
    if (format.indexOf('{{amount_with_comma_separator}}') !== -1) {
      return format.replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
    }
    return format.replace('{{amount}}', amount);
  }

  function quoteLines(lines, catalog) {
    var priced = [];
    var missing = [];
    var unavailable = [];
    var totalCents = 0;
    (lines || []).forEach(function (line) {
      var qty = Math.max(0, Math.floor(Number(line.quantity) || 0));
      if (!qty) return;
      var resolved = null;
      if (line.variantId && catalog.byId[line.variantId]) {
        resolved = catalog.byId[line.variantId];
      } else if (line.entry) {
        resolved = resolveCatalogEntry(line.entry, catalog);
      }
      if (!resolved) {
        missing.push(line.label || line.key || 'item');
        priced.push({
          key: line.key,
          label: line.label,
          quantity: qty,
          variantId: null,
          priceCents: null,
          lineCents: null,
          available: false,
          priced: false,
        });
        return;
      }
      if (!resolved.available) unavailable.push(line.label || resolved.title || resolved.id);
      var lineCents = resolved.priceCents * qty;
      totalCents += lineCents;
      priced.push({
        key: line.key,
        label: line.label,
        quantity: qty,
        variantId: resolved.id,
        sku: resolved.sku,
        priceCents: resolved.priceCents,
        lineCents: lineCents,
        available: resolved.available,
        priced: true,
        money: formatMoney(resolved.priceCents, catalog.moneyFormat),
        lineMoney: formatMoney(lineCents, catalog.moneyFormat),
      });
    });
    return {
      lines: priced,
      totalCents: totalCents,
      totalMoney: formatMoney(totalCents, catalog.moneyFormat),
      complete: missing.length === 0 && unavailable.length === 0,
      missing: missing,
      unavailable: unavailable,
      catalogLoaded: !!catalog.loaded,
    };
  }

  function assertCartItemsSafe(items, catalog) {
    var safe = [];
    var rejected = [];
    (items || []).forEach(function (item) {
      var id = toVariantId(item && item.id);
      var qty = Math.floor(Number(item && item.quantity) || 0);
      if (!id || qty < 1) {
        rejected.push('invalid_item');
        return;
      }
      var known = catalog.byId[id];
      if (!known) {
        rejected.push('unknown_variant:' + id);
        return;
      }
      if (!known.available) {
        rejected.push('unavailable_variant:' + id);
        return;
      }
      safe.push({
        id: id,
        quantity: qty,
        properties: item.properties && typeof item.properties === 'object' ? item.properties : {},
      });
      var parentId = toVariantId(item && item.parent_id);
      if (parentId) safe[safe.length - 1].parent_id = parentId;
    });
    return {
      valid: rejected.length === 0 && safe.length > 0,
      items: safe,
      rejected: rejected,
    };
  }

  window.LEAVES_CONFIGURATOR_PRICING = {
    emptyCatalog: emptyCatalog,
    collectProductHandles: collectProductHandles,
    loadCatalog: loadCatalog,
    resolveCatalogEntry: resolveCatalogEntry,
    applyVariantMapToRules: applyVariantMapToRules,
    entryIsMapped: entryIsMapped,
    formatMoney: formatMoney,
    quoteLines: quoteLines,
    assertCartItemsSafe: assertCartItemsSafe,
    toVariantId: toVariantId,
    cableKey: cableKey,
  };
})(window);
